import { Spacing } from '@persona-id/design-tokens';
import { CircleHelpIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { difference } from 'lodash';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import HelpIcon from 'dashboard/components-lib/HelpIcon';
import {
  AgeLimitCheckConfig,
  AgeLimitConfig,
} from 'dashboard/models/verification-check-config/id-age-comparison';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import Button from 'design-system/components/Button/Button';
import Card from 'design-system/components/Card';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';

const DEFAULT = 'default';

const AgeLimitContainer = styled.div`
  padding: ${Spacing[5]};
  border-radius: ${BORDER_RADIUSES.rounded};
  background-color: ${({ theme }) => theme.color.background.secondary};
  margin-bottom: ${Spacing[2]};
`;

interface AgeComparisonConfigurationElementProps {
  verificationCheck: AgeLimitCheckConfig;
  editable: boolean;
  handleUpdateCheck: (verificationCheck: VerificationCheckConfig) => void;
}

const AgeComparisonConfigurationElement = ({
  verificationCheck,
  editable,
  handleUpdateCheck,
}: AgeComparisonConfigurationElementProps) => {
  const ageLimits = produce(verificationCheck['age-limits'] || [], (draftAgeLimits) => {
    if (!draftAgeLimits.find((ageLimit) => ageLimit['country-code'] === DEFAULT)) {
      draftAgeLimits.unshift({
        'country-code': DEFAULT,
        'min-age': null,
        'max-age': null,
      });
    } else {
      draftAgeLimits.sort((ageLimit) => (ageLimit['country-code'] === DEFAULT ? 0 : 1));
    }
  });
  const handleAddAgeLimit = () => {
    const newAgeLimits = produce(ageLimits, (draftAgeLimits) => {
      draftAgeLimits.push({
        'country-code': null,
        'min-age': null,
        'max-age': null,
      });
    });
    handleUpdateCheck({
      ...verificationCheck,
      'age-limits': newAgeLimits,
    } as AgeLimitCheckConfig);
  };
  return (
    <Card>
      <Card.Body>
        <Card.Title>
          <Paragraph size='md' weight='bold' color={editable ? 'primary' : 'disabled'}>
            Age Ranges
          </Paragraph>
          <HelpIcon icon={<CircleHelpIcon />}>
            Any countries that do not have age limits defined will use the defaults age limits.
          </HelpIcon>
        </Card.Title>
        {ageLimits.map((ageLimit, i) => (
          <AgeLimitCard
            key={i}
            ageLimit={ageLimit}
            editable={editable}
            selectableCountryCodes={
              difference(
                Object.keys(COUNTRIES),
                ageLimits.map((testAgeLimit) => {
                  // The selected country must be one of the options
                  if (testAgeLimit['country-code'] !== ageLimit['country-code'])
                    return testAgeLimit['country-code'];
                }),
              ) as CountryCode[]
            }
            handleUpdateAgeLimit={(ageLimit: AgeLimitConfig) => {
              const newAgeLimits = produce(ageLimits, (draftAgeLimits) => {
                draftAgeLimits[i] = ageLimit;
              });
              handleUpdateCheck({
                ...verificationCheck,
                'age-limits': newAgeLimits,
              } as AgeLimitCheckConfig);
            }}
            handleDeleteAgeLimit={() => {
              const newAgeLimits = produce(ageLimits, (draftAgeLimits) => {
                draftAgeLimits.splice(i, 1);
              });
              handleUpdateCheck({
                ...verificationCheck,
                'age-limits': newAgeLimits,
              } as AgeLimitCheckConfig);
            }}
          />
        ))}
        {editable && (
          <Button
            id='age-limit-configuration-element__add-age-limit'
            variant='basic'
            width='button'
            disabled={!editable}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAddAgeLimit();
            }}
          >
            Add Country
          </Button>
        )}
      </Card.Body>
    </Card>
  );
};

interface AgeLimitCardProps {
  ageLimit: AgeLimitConfig;
  selectableCountryCodes?: CountryCode[];
  editable: boolean;
  handleUpdateAgeLimit: (ageLimit: AgeLimitConfig) => void;
  handleDeleteAgeLimit: () => void;
}

const AgeLimitCard = ({
  ageLimit,
  selectableCountryCodes,
  editable,
  handleUpdateAgeLimit,
  handleDeleteAgeLimit,
}: AgeLimitCardProps) => {
  const maxAge = ageLimit['max-age'];
  const minAge = ageLimit['min-age'];
  const countryCode = ageLimit['country-code'];
  const handleUpdateMinAge = (minAge: number) => {
    handleUpdateAgeLimit({
      ...ageLimit,
      'min-age': minAge || null,
    });
  };
  const handleUpdateMaxAge = (maxAge: number) => {
    handleUpdateAgeLimit({
      ...ageLimit,
      'max-age': maxAge || null,
    });
  };
  const handleUpdateCountry = (countryCode: CountryCode) => {
    handleUpdateAgeLimit({
      ...ageLimit,
      'country-code': countryCode,
    });
  };
  return (
    <AgeLimitContainer>
      {countryCode === DEFAULT ? (
        <Paragraph weight='bold' color={editable ? 'primary' : 'disabled'}>
          Default Age Limits
        </Paragraph>
      ) : (
        <>
          <Label color={editable ? 'primary' : 'disabled'}>Country:</Label>
          <Spacer height={1} />
          <Select
            size='md'
            placeholder='--'
            value={countryCode}
            onValueChange={handleUpdateCountry}
          >
            {(selectableCountryCodes || Object.keys(COUNTRIES)).map((countryCode) => (
              <Option key={countryCode} value={countryCode}>
                {countryCode}
              </Option>
            ))}
          </Select>
        </>
      )}
      <Spacer height={1} />
      <Label color={editable ? 'primary' : 'disabled'}>Minimum Age:</Label>
      <Spacer height={1} />
      <Input
        size='md'
        type='number'
        disabled={!editable}
        value={minAge || ''}
        onChange={(e) => handleUpdateMinAge(+e.target.value)}
        placeholder='No min age'
      />
      <Spacer height={1} />
      <Label color={editable ? 'primary' : 'disabled'}>Maximum Age:</Label>
      <Spacer height={1} />
      <Input
        size='md'
        type='number'
        disabled={!editable}
        value={maxAge || ''}
        onChange={(e) => handleUpdateMaxAge(+e.target.value)}
        placeholder='No max age'
      />
      <Spacer height={2} />
      {countryCode !== DEFAULT && editable && (
        <Button
          id='age-limit-configuration-element__remove-age-limit'
          variant='basic'
          width='button'
          disabled={!editable}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDeleteAgeLimit();
          }}
        >
          Remove Age Limit
        </Button>
      )}
    </AgeLimitContainer>
  );
};

export default AgeComparisonConfigurationElement;
